import React, {useState, useEffect, useRef} from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import DocInfo from "./EditCenterPanels/EditAreaPanels/DocInfo";
import Editor from "../Common/Editor";
import PopUp from "../Common/PopUp";
import { showToast } from "../Common/Messages";
import SearchDocument from "../Common/SearchDocument";
import { excludeKeys } from "../libs/Util";
import DocHistory from "./EditCenterPanels/EditAreaPanels/DocHistory";
import { PreviewButton } from "./EditCenterPanels/EditUtils";

const QAEditor = (props) => {
    const LoginStates = useSelector(state => state.login);
    const params = useParams();
    const [valid, setValid] = useState({isValid: true, message: ""});
    const [sidePanelType, setSidePanelType] = useState("qainfo");   
    const [isViewMode, setIsViewMode] = useState(true);
    const [popUpContent, setPopUpContent] = useState(null);
    const validRef = useRef(valid);

    const [current, setCurrent] = useState(null);
    const [originData, setOriginData] = useState(null);
    const [editorData, setEditorData] = useState(null);
    const [accessType, setAccessType] = useState([]);

    useEffect(() => {
        const controller = new AbortController();
        fetch('datas/oper/accesstype.json', {
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Cache-Control': 'no-cache'
            },
            signal: controller.signal
        })
            .then(response => {
                if (!response.ok) throw new Error('Failed to load access types');
                return response.json();
            })
            .then(data => {
                if (!Array.isArray(data)) throw new Error('Invalid access types');
                if (!controller.signal.aborted) setAccessType(data);
            })
            .catch(() => {
                if (!controller.signal.aborted) {
                    showToast('danger', '접근 권한 목록을 불러오지 못했습니다.', 3000);
                }
            });
        return () => controller.abort();
    }, []);

    useEffect(() => {
        validRef.current = valid;
    }, [valid]);

    useEffect(() => {
        if(params.id) {
            getManual(params.id);
        }
    }, [LoginStates]);
    
    const getManual = async (id) => {
        const body = {            
            qAId: id
        };
        const response = await fetch('api/Manager/GetQAManual', {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json; charset=utf-8'
            },
            body: JSON.stringify(body)
        });
        const result = await response.json(); 
        if(response.status === 200 && result.message === "Success"){
            if(result.data.table.length > 0) {
                const data = result.data.table[0];
                const content = data.contentJson !== '' ? JSON.parse(data.contentJson) : {};
                delete content.versionInfo;
                delete content.menuInfo;
                delete content.qAId;
                delete content.qAStatus;
                delete content.workId;
                delete content.workStatus;
                delete content.updateDate;
                delete content.updateUserId;
                delete content.updateUserName;

                setIsViewMode(data.qaStatus !== "2");
                setOriginData(content);
                setEditorData(content);    
                setCurrent(data);
            }
        } else {
            const error = result.error;
            if(error === "Fail Get Document") {
                showToast('danger', "문서 정보가 서버에 존재하지 않습니다.");
            } else {
                showToast('danger', "문서를 조회할 수 없습니다.");
            }
        }
    }

    const checkIsChange = (type) => {   
        const origin = excludeKeys(originData, ['time', 'id']);
        const edit = excludeKeys(editorData, ['time', 'id']);
        const isEmpty = Object.keys(origin.data).length === 0;
        const isChange = !isEmpty && JSON.stringify(origin) !== JSON.stringify(edit); 
        if(type === "End") {
            if(isChange) {
                const isContinue = window.confirm("변경된 내용이 존재합니다. 저장 후 완료하시겠습니까?");
                return isContinue;
            }
        } 
        return true;
    }

    const handleChange = (e) => {
        setEditorData(prev => {return {...prev, title: e.target.value}});
    }

    const addRelatedDoc = (item, type) => {
        setEditorData(prev => { return {...prev, [type]: [...(prev[type] || []), item]} });
    }

    const openSearchDoc = (from, type="relation") => {
        let documents = "";
        if(from === "faq") {
            documents = editorData.faq ?? [];
        } else {
            documents = editorData.relationInfo;
        }
        if(current && !isViewMode)
            setPopUpContent(<SearchDocument from={from} type={type} documents={documents} 
                addItem={addRelatedDoc} removeItem={removeRelatedDoc} fnBtnOnPopup={closeRelatedDoc} />);
    }

    const removeRelatedDoc = (item, type) => {
        if (type === "relationInfo") {
            const relatedDocumentData = editorData.relationInfo?.filter(doc => doc.manualId !== item.manualId);
            setEditorData(prev => {return {...prev, relationInfo: relatedDocumentData}});
        } else {
            const faqDocumentData = editorData.faq?.filter(doc => doc.faqID !== item.faqID);
            setEditorData(prev => {return {...prev, faq: faqDocumentData}});
        }
    }

    const closeRelatedDoc = () => {
        setPopUpContent(null);
    }
    
    const setOpen = (name) =>{
        if(sidePanelType === name)
            setSidePanelType("");
        else
            setSidePanelType(name);
    }

    const viewSidePanel = () => {
        const panel = sidePanelType === 'info' ?
                <DocInfo openSearchDoc={openSearchDoc} removeRelatedDoc={removeRelatedDoc} isViewMode={isViewMode} 
                    docData={editorData} setDocData={setEditorData} accessType={accessType} onClose={() => setSidePanelType("")}/>
            :sidePanelType === 'history' ?
                <DocHistory docId={current.docID} onClose={() => setSidePanelType("")}/>
            :sidePanelType === 'qainfo' ? 
                <QAInfo current={current} onClose={() => setSidePanelType("")} />
            :null
        return panel;
    }

    const onQADocument = async (workingTag, requestData, comment="") => {
        let status = "1";
        switch (workingTag) {
            case "Work":
            case "Update":
            case "CancelWork":
                status = "2";
                break;
            case "Complete":
                status = "3";
                break;
            case "Reject":
                status = "4";
                break;
            default:
                status = "1";
                break;
        }
        const body = {
            workingTag: workingTag,
            workId: current.workID,
            qAId: current.qaid || "",
            qAStatus: status,
            devUserId: LoginStates.userId,
            menuPath: requestData?.path?.menuPath || "",
            location: requestData?.path?.location || "",
            fullPath: JSON.stringify(requestData?.path?.fullPath || []),
            title: requestData?.title || "",
            infomations: JSON.stringify(requestData?.type ? {
                product: requestData?.type?.type || "",
                version: requestData?.sub || "",
            } : {}),
            comment: requestData?.comment || "",
            contentJson: JSON.stringify(editorData || {}),
            qAComment: comment
        };
        const response = await fetch('api/Manager/QADocument', {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json; charset=utf-8'
            },
            body: JSON.stringify(body)
        });
        const result = await response.json();
        if(response.status === 200){
            showToast('success', result.message);
            getManual(current.qaid);
        } else {
            showToast('danger', result.message);
        }
        if(workingTag === "Request") {
            setPopUpContent(null);
        }
    }

    return (
        current &&
        <div className="manual-editor"> 
            <div className="edit-content">
                <div className={'edit-area'}>
                    <QAEditProperty ViewMode={isViewMode} current={current} setCurrent={setCurrent} docData={editorData} checkIsChange={checkIsChange} 
                        onQADocument={onQADocument} setOpen={setOpen} menuName={current.docBoxName} />

                    <div className={"editor-containder-wrap"}>
                        <div className={"editor-container"}>                
                            <div className={"editor-section"}>
                                <div className={"title-section"}>
                                    <textarea name="title" placeholder={editorData?.title ?? "제목"} 
                                        value={editorData?.title ?? ""} onChange={handleChange} readOnly={isViewMode} />
                                </div>
                                <Editor ViewMode={isViewMode} folderName={current.docBoxPath} setEditorData={setEditorData} editorData={editorData} 
                                    docId={current.docID} setValid={setValid} />
                            </div>
                        </div>    
                        {viewSidePanel()}
                    </div>
                    <PopUp content={popUpContent} setContent={setPopUpContent} />
                </div>
            </div>
        </div>
    )
}
export default QAEditor;

const QAEditProperty = (props) => {
    const [morePopUp, setMorePopUp] = useState(null);
    const [buttonList, setButtonList] = useState(null);
    const [openQAInfo, setOpenQAInfo] = useState(false);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const infoRef = useRef();

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        }
    }, []);

    useEffect(() => {
        if (!props.current) return;
        const buttons = [];
        if (props.current.qaStatus === "1") {
            buttons.push(<button className="btn-primary" onClick={e => RequestQA(e, 'Work')}>검수 시작</button>)
        } else if (props.current.qaStatus === "2") {
            buttons.push(<button className="btn-secondary" onClick={e => {if(window.confirm("검수를 취소하시겠습니까?")) RequestQA(e, 'Cancel')}}>검수 취소</button>)
            buttons.push(<button className="btn-secondary" onClick={e => RequestQA(e, 'Update')}>저장</button>)
            buttons.push(<button className="btn-primary" onClick={e => {if(props.checkIsChange("End"))EndQA('Complete')}}>검수 완료</button>)
            buttons.push(<button className="btn-nega btn-secondary" onClick={e => EndQA('Reject')}>반려</button>)
        }
        setButtonList(buttons);

    }, [props.ViewMode, props.docData]);

    const handleClickOutside = (event) => {
        if (infoRef.current && !infoRef.current.contains(event.target)) {
            setOpenQAInfo(false);
        }
    }

    const RequestQA = (e, workingTag, comment) => {
        e.preventDefault();
        props.onQADocument(workingTag, null, comment);
    }

    const EndQA = (workingTag) => {
        let comment = "";
        const handleChange = (e) => {
            comment = e.target.value;

        }
        setMorePopUp(
            <div className="request">
                <div className="request-modal">
                    <div className="request-top">
                        <h3 className='request-title'>{`${workingTag === "Complete" ? '검수' : '반려'} 코멘트 입력`}</h3>
                        <button className="btn-close" onClick={() => setMorePopUp(null)}>
                            <i className="icoKdc32 close"></i>
                        </button>
                    </div>
                    <div className="request-cnts">
                        <div className="memo">
                            <div className="cate-top">
                                <h4 className="cate-title">코멘트</h4>
                            </div>
                            <input className="input-memo" type="text" id="name" name="comment" placeholder="코멘트 내용을 입력하세요." onChange={handleChange} />
                        </div>
                    </div>
                    <button className="btn-request" onClick={(e) => {
                        RequestQA(e, workingTag, comment);
                        setMorePopUp(null);
                    }}>완료</button>
                </div>
            </div>
        )
    }

    const closeClick = (e) => {
        window.close();
    }

    const EditButtons = () => {
        return (
            <div className="btn-wrap" >
                <PreviewButton docData={props.docData} docBoxPath={props.current?.docBoxPath} docId={props.current?.docID} />
                <button className="btn-icon" onClick={e => props.setOpen('qainfo')} ><i className='icoKdc24 docinfo'></i></button>
                <button className="btn-icon" onClick={e => props.setOpen('info')} ><i className='icoKdc24 info'></i></button>
                <button className="btn-icon" onClick={e => props.setOpen('history')} ><i className='icoKdc24 history'></i></button>
                {buttonList}
                <button className="btn-close" onClick={closeClick} style={{background: "transparent"}}><i className='icoKdc32 close'></i></button>
            </div>
        );
    }

    const menuInfo = () => {
        const path = (fullPath) => {
            const menuPath = [];
            for (var item in fullPath) {
                if (item > 0) {
                    menuPath.push(<i className='icoKdc12 next'></i>)
                }
                menuPath.push(<span>{fullPath[item]}</span>)
            }
            return menuPath;
        }
        const menuData = props.current?.menuInfo !== "" ? JSON.parse(props.current.menuInfo) : [];
        return menuData.length > 0 && (
            <div className='posted-doc'>
                <div className='badge-posted'>
                    <i className='icoKdc20 posted'></i>
                    <p>게시된 문서</p>
                </div>
                <p>게시경로</p>
                <div className='posted-link-wrap'>
                    {menuData.map(menu => {
                        return (
                            <div className='posted-link' onClick={() => window.open(`/${menu.menuPath}/${menu.manualID}`, "_blank")}>
                                {path(menu.fullPath)}
                            </div>
                        )
                    })}
                </div>
            </div>
        )
    }

    return (
        <div className="property">
            <header className='editor-header'>
                <div className="docinfo-wrap">
                    <div className='path-wrap'>
                        <i className='icoKdc24 folder'></i>
                        <div className="path">
                            <span>{props.menuName}</span>
                        </div>
                    </div>
                    <div className='divide'></div>
                </div>
                {EditButtons()}
                <PopUp content={morePopUp} setContent={setMorePopUp} />
            </header>
            {menuInfo()}
        </div>
    );
};

const QAInfo = (props) => {
    const infoContent = [
        { title: '요청 경로', type: 'path' },
        { title: '요청 메뉴명', type: 'title' },
        { title: '요청 정보', type: 'infomation' },
        { title: '요청 코멘트', type: 'comment' },
        { title: '요청자', type: 'requestUserName' },
        { title: '요청일', type: 'requestDate' }
    ];
    const boxStyle={
        display: "flex",
        alignItems: "center",
        gap: "4px",
        fontSize: "14px",
        fontWeight: "500",
        color: "#333",
        lineHeight: "150%",
        borderRadius: "8px",
        background: "#f8f8f8",
        padding: "8px"
    }
    const qAInfo = props.current?.qaInfo ? JSON.parse(props.current.qaInfo) : {};

    const isEmpty = (value) => {
        if (Array.isArray(value)) return value.length === 0;
        if (typeof value === 'object') return Object.keys(value).length === 0;
        return !value;
    };

    const getInfoValue = (value, type) => {
        if(type === "path") {
            return value.map((x, index) => {
                return (
                    <React.Fragment key={index}>
                        <span>{x}</span>
                        {index < value.length - 1 && <i className="icoKdc12 next"></i>}
                    </React.Fragment>
                )
            })
        } else if (type === "infomation") {
            const entries = Object.entries(value)
                .filter(([_, val]) => val && val.trim() !== ''); // 빈 값 제거
            return (
                <p>
                    {entries.map(([key, value], index) => {
                        let info = value;
                        if (key==="product") info = "K-System " + value.charAt(0).toUpperCase() + value.slice(1);
                        return index < entries.length - 1 ? `${info}, ` : info;
                    })}
                </p>
            )
        } else {
            return (<p>{value}</p>)
        }
        
    }

    return Object.keys(qAInfo).length > 0 && (
        <div className="editor-side">
            <div className="editor-side-top">
                <h3 className='title'>요청 정보</h3>
                <button className="btn-close" onClick={props.onClose}>
                    <i className="icoKdc32 close"></i>
                </button>
            </div>
            <div className="editor-side-cnts">
                {infoContent.map((info, index) => {
                    const qAInfoData = qAInfo[info.type];
                    return !isEmpty(qAInfoData) && (
                        <div key={index} className='doclist-wrap'>
                            <div className="cate-top">
                                <h4 className="cate-title">{info.title}</h4>
                            </div>
                            <div className='doc-info-list-cnts'>
                                <div style={boxStyle}>
                                    {getInfoValue(qAInfoData, info.type)}
                                </div>
                            </div>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}
